import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    OutboundProvisioningStep,
    OutboundProvisioningStepStatus,
    OutboundSchedulePolicy,
    runOutboundProvisioning,
    defaultOutboundSchedulePolicy,
    parseSchedulePolicyJson
} from '../../services/outboundAutomation';
import ModalLayout from '../../../../shared/components/structure/ModalLayout';
import ModalHeader from '../../../../shared/components/general/ModalHeader';
import Button from '../../../../shared/components/general/Button';
import Loader from '../../../../shared/components/general/Loader';
import { color_shades_dark, color_shades_darker, color_defaults_white } from '../../../../shared/constants/colors';
import { style_border_default } from '../../../../shared/constants/stylesValues';
import { ScheduleBuilder } from './ScheduleBuilder';
import Switch from '../../../../shared/components/forms/Switch';
import { getTelephonyDeployments, findApplication, findApplicationWithFeatures, getAppliedApplicationTemplatesForApp, getFeatureFlags } from '../../../../shared/api';
import { OutboundCallFeatureFlagId } from '../../../../shared/constants/featureFlagIds';
import { breakpoint_small } from '../../../../shared/constants/breakpoints';

const SchedulePolicySettingId = 'f3a2b1c0-d4e5-6f7a-8b9c-0d1e2f3a4b5c';

export interface OutboundProvisioningProgressModalProps {
    applicationId: string;
    environmentId: string;
    organizationName: string;
    locationName: string;
    appName: string;
    pmsName: string;
    defaultOfficeId: string;
    timeZone: string;
    medicalIntegrationConfigId: string;
    hasSpanish: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    orgId?: string;
    deferProvisioning?: boolean;
    initialSchedulePolicy?: OutboundSchedulePolicy;
    initialOutboundCallingEnabled?: boolean;
    applicationFeatureFlags?: FeatureFlagModel[];
    onSaveConfig?: (schedulePolicy: OutboundSchedulePolicy, outboundCallingEnabled: boolean) => void;
}

interface StepItem {
    id: OutboundProvisioningStep;
    label: string;
    description: string;
}

const stepsList: StepItem[] = [
    { id: 'preflight', label: 'Configuration Check', description: 'Checking required outbound setup details' },
    { id: 'app-settings', label: 'App Settings', description: 'Enable Outbound calling and set location fields' },
    { id: 'environment-settings', label: 'Environment Settings', description: 'Apply settings to Production environment' },
    { id: 'english-outbound-phone', label: 'English Outbound Phone', description: 'Create and configure English outbound Telnyx number' },
    { id: 'spanish-outbound-phone', label: 'Spanish Outbound Phone', description: 'Create Spanish outbound number with voice settings (if applicable)' },
    { id: 'feature-settings', label: 'Feature Settings', description: 'Configure outbound call parameters and integration overrides' },
    { id: 'schedule-policy', label: 'Schedule Policy', description: 'Build and format outbound calling schedule JSON' },
    { id: 'module-updates', label: 'Module Updates', description: 'Update callback number override in Spark module fields' },
    { id: 'final-activation', label: 'Final Activation', description: 'Enable outbound call processing on outbound lines' }
];

export const OutboundProvisioningProgressModal: React.FC<OutboundProvisioningProgressModalProps> = (props) => {
    const [stepStatuses, setStepStatuses] = useState<Record<OutboundProvisioningStep, OutboundProvisioningStepStatus>>({
        'preflight': 'pending',
        'app-settings': 'pending',
        'environment-settings': 'pending',
        'english-outbound-phone': 'pending',
        'spanish-outbound-phone': 'pending',
        'feature-settings': 'pending',
        'schedule-policy': 'pending',
        'module-updates': 'pending',
        'final-activation': 'pending'
    });
    const [stepErrors, setStepErrors] = useState<Partial<Record<OutboundProvisioningStep, string>>>({});
    const [isRunning, setIsRunning] = useState(false);
    const [isLoadingConfig, setIsLoadingConfig] = useState(true);
    const [schedulePolicy, setSchedulePolicy] = useState<OutboundSchedulePolicy>(props.initialSchedulePolicy || defaultOutboundSchedulePolicy);
    const [showConfig, setShowConfig] = useState(true);
    const [outboundCallingEnabled, setOutboundCallingEnabled] = useState(!!props.initialOutboundCallingEnabled);

    useEffect(() => {
        let isMounted = true;

        const checkExistingLiveState = async () => {
            setIsLoadingConfig(true);

            try {
                // Only load calling enabled state if not deferred
                if (!props.deferProvisioning) {
                    const deploymentsResult = await getTelephonyDeployments(props.applicationId);
                    if (deploymentsResult.resultType === 'Ok' && deploymentsResult.data) {
                        const outboundEN = deploymentsResult.data.find(d => d.locale === 'en-US' && d.useForOutboundCalling);
                        if (outboundEN && isMounted) {
                            setOutboundCallingEnabled(!!outboundEN.outboundCallingEnabled);
                        }
                    }
                }

                // ALWAYS load schedule policy if not passed initially as prop
                if (!props.initialSchedulePolicy) {
                    // Helper to extract policy from app
                    const extractPolicy = (app: any) => {
                        const outboundFlag = app.featureFlags?.find((ff: any) => ff.id === OutboundCallFeatureFlagId || ff.featureFlagId === OutboundCallFeatureFlagId);
                        if (outboundFlag && outboundFlag.featureFlagSettings) {
                            const scheduleSetting = outboundFlag.featureFlagSettings.find((s: any) => s.id === SchedulePolicySettingId || s.featureFlagSettingId === SchedulePolicySettingId);
                            if (scheduleSetting && scheduleSetting.value) {
                                const normalizedPolicy = parseSchedulePolicyJson(scheduleSetting.value);
                                if (normalizedPolicy) {
                                    return normalizedPolicy;
                                }
                            }
                        }
                        return null;
                    };

                    // 1. Try the already-loaded app state first. Feature Settings saves into currentApp.featureFlags.
                    const statePolicy = extractPolicy({ featureFlags: props.applicationFeatureFlags });
                    if (statePolicy) {
                        if (isMounted) setSchedulePolicy(statePolicy);
                        return;
                    }

                    // 2. Try the same application endpoint used by Feature Settings/app state.
                    const plainAppResult = await findApplication(props.applicationId);
                    if (plainAppResult.resultType === 'Ok' && plainAppResult.data) {
                        const policy = extractPolicy(plainAppResult.data);
                        if (policy) {
                            if (isMounted) setSchedulePolicy(policy);
                            return;
                        }
                    }

                    // 3. Try to load from Location Application with expanded features
                    const appResult = await findApplicationWithFeatures(props.applicationId);
                    if (appResult.resultType === 'Ok' && appResult.data) {
                        const policy = extractPolicy(appResult.data);
                        if (policy) {
                            if (isMounted) setSchedulePolicy(policy);
                            return;
                        }
                    }

                    // 4. Fallback: Load from Parent Template Application
                    const templatesResult = await getAppliedApplicationTemplatesForApp(props.applicationId);
                    if (templatesResult.resultType === 'Ok' && templatesResult.data && templatesResult.data.length > 0) {
                        const parentAppId = templatesResult.data[0]?.templateForm?.templateConfiguration?.templateApplicationId;
                        if (parentAppId) {
                            const parentAppResult = await findApplicationWithFeatures(parentAppId);
                            if (parentAppResult.resultType === 'Ok' && parentAppResult.data) {
                                const policy = extractPolicy(parentAppResult.data);
                                if (policy) {
                                    if (isMounted) setSchedulePolicy(policy);
                                }
                            }
                        }
                    }

                    // 5. Fallback: Feature Settings renders from the master feature flag list when no app override exists
                    const featureFlagsResult = await getFeatureFlags();
                    if (featureFlagsResult.resultType === 'Ok' && featureFlagsResult.data) {
                        const policy = extractPolicy({ featureFlags: featureFlagsResult.data });
                        if (policy) {
                            if (isMounted) setSchedulePolicy(policy);
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to check existing outbound live state:", err);
            } finally {
                if (isMounted) setIsLoadingConfig(false);
            }
        };
        checkExistingLiveState();

        return () => {
            isMounted = false;
        };
    }, [props.applicationId, props.deferProvisioning, props.initialSchedulePolicy, props.applicationFeatureFlags]);

    const handleStartProvisioning = async () => {
        if (props.deferProvisioning) {
            if (props.onSaveConfig) {
                props.onSaveConfig(schedulePolicy || { dayOfWeekOverrides: [] }, outboundCallingEnabled);
            }
            return;
        }

        setIsRunning(true);
        setShowConfig(false);

        // Reset step statuses to pending or in_progress
        const initialStatuses = { ...stepStatuses };
        stepsList.forEach(s => {
            initialStatuses[s.id] = 'pending';
        });
        setStepStatuses(initialStatuses);
        setStepErrors({});

        const res = await runOutboundProvisioning(
            {
                applicationId: props.applicationId,
                environmentId: props.environmentId,
                organizationName: props.organizationName,
                locationName: props.locationName,
                appName: props.appName,
                pmsName: props.pmsName,
                defaultOfficeId: props.defaultOfficeId,
                timeZone: props.timeZone,
                medicalIntegrationConfigId: props.medicalIntegrationConfigId,
                schedulePolicy: schedulePolicy || { dayOfWeekOverrides: [] },
                hasSpanish: props.hasSpanish,
                outboundCallingEnabled: outboundCallingEnabled,
                orgId: props.orgId
            },
            (step, status, error) => {
                setStepStatuses(prev => ({ ...prev, [step]: status }));
                if (error) {
                    setStepErrors(prev => ({ ...prev, [step]: error }));
                }
            }
        );

        setIsRunning(false);
        if (res.success) {
            if (props.onSuccess) props.onSuccess();
        }
    };

    const hasAnySchedule = schedulePolicy?.dayOfWeekOverrides?.some(o => o.timeWindows && o.timeWindows.length > 0) ?? false;
    const preflightError = stepErrors.preflight;

    const getStatusIndicator = (status: OutboundProvisioningStepStatus) => {
        switch (status) {
            case 'pending':
                return <Indicator color="#ccc">○</Indicator>;
            case 'in_progress':
                return <LoaderContainer><Loader /></LoaderContainer>;
            case 'completed':
                return <Indicator color="green">●</Indicator>;
            case 'failed':
                return <Indicator color="red">▲</Indicator>;
        }
    };

    return (
        <ModalLayout isVisible={true} onClose={props.onClose}>
            <HeaderWrapper>
                <ModalHeader title="Set the Outbound Schedule" onClose={props.onClose} />
            </HeaderWrapper>
            
            <ScrollableContent>
                {showConfig ? (
                    isLoadingConfig ? (
                        <LoadingConfigWrapper>
                            <Loader />
                        </LoadingConfigWrapper>
                    ) : (
                        <>
                        <ScheduleBuilder
                            value={schedulePolicy}
                            onChange={(policy) => setSchedulePolicy(policy)}
                        />
                        <SwitchWrapper style={{ marginTop: '24px' }}>
                            <Switch
                                label="Enable Outbound Calling (Go Live)"
                                detail="Toggle on when you are ready to begin outbound dialing and automated confirm calls. Keep disabled to set up and save your schedule in draft mode."
                                checked={outboundCallingEnabled}
                                onChange={(checked) => setOutboundCallingEnabled(checked)}
                                disabled={isRunning}
                            />
                        </SwitchWrapper>
                        </>
                    )
                ) : (
                    preflightError ? (
                        <FormValidationError>{preflightError}</FormValidationError>
                    ) : (
                        <StepsWrapper>
                            <List>
                                {stepsList.map(step => {
                                    // Skip Spanish step if hasSpanish is false
                                    if (step.id === 'spanish-outbound-phone' && !props.hasSpanish) {
                                        return null;
                                    }

                                    const status = stepStatuses[step.id];
                                    const error = stepErrors[step.id];

                                    return (
                                        <StepRow key={step.id} status={status}>
                                            <IndicatorColumn>
                                                {status === 'in_progress' ? <LoaderContainer><Loader /></LoaderContainer> : getStatusIndicator(status)}
                                            </IndicatorColumn>
                                            <TextColumn>
                                                <Label>{step.label}</Label>
                                                <Desc>{step.description}</Desc>
                                                {error && <FormValidationError>{error}</FormValidationError>}
                                            </TextColumn>
                                        </StepRow>
                                    );
                                })}
                            </List>
                        </StepsWrapper>
                    )
                )}
            </ScrollableContent>

            {showConfig ? (
                <FooterWrapper>
                    <Button
                        themes={["secondary"]}
                        text="Cancel"
                        onClick={props.onClose}
                        disabled={isRunning}
                    />
                    <Button
                        themes={["primary"]}
                        text={props.deferProvisioning ? "Save Schedule" : outboundCallingEnabled ? "Start Outbound Calls" : "Save Draft Schedule"}
                        onClick={handleStartProvisioning}
                        disabled={isRunning || isLoadingConfig || !hasAnySchedule}
                    />
                </FooterWrapper>
            ) : (
                <FooterWrapper>
                    {!isRunning && Object.values(stepStatuses).some(s => s === 'failed') && (
                        <Button
                            themes={["primary"]}
                            text="Retry From Failure"
                            onClick={handleStartProvisioning}
                        />
                    )}
                    <Button
                        themes={["secondary"]}
                        text={Object.values(stepStatuses).every(s => s === 'completed') ? "Close" : "Close Process"}
                        onClick={props.onClose}
                        disabled={isRunning}
                    />
                </FooterWrapper>
            )}
        </ModalLayout>
    );
};

const HeaderWrapper = styled.div`
    position: relative;
    z-index: 10;
    background-color: ${color_defaults_white};
`;

const ScrollableContent = styled.div`
    padding: 24px;
    flex: 1;
    overflow-y: auto;
    background-color: #fafbfc;
`;

const LoadingConfigWrapper = styled.div`
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StepsWrapper = styled.div`
    background-color: ${color_defaults_white};
    padding: 24px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02);
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const StepRow = styled.div<{ status: OutboundProvisioningStepStatus }>`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 20px;
    border-radius: 8px;
    border: 1px solid ${props => 
        props.status === 'completed' ? '#c6f6d5' : 
        props.status === 'failed' ? '#fed7d7' : 
        props.status === 'in_progress' ? '#bee3f8' : '#e2e8f0'};
    background-color: ${props => 
        props.status === 'completed' ? '#f0fff4' : 
        props.status === 'failed' ? '#fff5f5' : 
        props.status === 'in_progress' ? '#ebf8ff' : '#ffffff'};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: ${props => 
        props.status === 'in_progress' ? '0 4px 12px rgba(66, 153, 225, 0.12)' : 'none'};
`;

const IndicatorColumn = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
`;

const TextColumn = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const Label = styled.div`
    font-weight: bold;
    font-size: 14px;
    color: ${color_shades_darker};
`;

const Desc = styled.div`
    font-size: 12px;
    color: ${color_shades_dark};
    margin-top: 4px;
`;

const Indicator = styled.span<{ color: string }>`
    font-size: 18px;
    color: ${props => props.color};
    line-height: 1;
`;

const LoaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const FormValidationError = styled.div`
    font-size: 14px;
    color: #d32f2f;
    margin-top: 12px;
    padding: 8px 12px;
    background-color: #ffebee;
    border-radius: 4px;
    border-left: 4px solid #d32f2f;
    font-weight: 500;
    line-height: 1.4;
`;

const FooterWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    background-color: ${color_defaults_white};
    border-top: 1px solid #e2e8f0;
    z-index: 10;

    ${breakpoint_small} {
        padding: 12px 16px;
        flex-direction: column-reverse;
        button {
            width: 100%;
        }
    }
`;

const SwitchWrapper = styled.div`
    background-color: ${color_defaults_white};
    padding: 20px 24px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    margin-bottom: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02);
`;
